import React, { useState, useMemo } from "react"
import { Box, Collapse, Divider, IconButton, makeStyles, Popover, TextField, useMediaQuery } from "@material-ui/core"
import moment from "moment"
import pluralize from "pluralize"
import { Icon, Caption, FormatDateTimeCompact, DeleteConfirmIconButton } from ".."
import { useMakeJobStepOptimistic, useMutationJobStepAddNote, useMutationJobStepDeleteNote } from "../../data"
import { useAuth } from "../../services"
import { ColumnBox, FlexBox, RowBox } from "../Boxes"
import { Avatar } from "../Avatar"
import FooterButton from "../Buttons/ActionCardFooterButton"
import { toId, preventDefault, useJobUtils } from "../../utils"

const useStyles = makeStyles((theme) => ({
  noNotesIcon: {
    fontSize: 20,
  },
  hasNotesIcon: {
    color: theme.palette.raised.main,
    fontSize: 20,
  },
  popover: {
    minWidth: 300,
    [theme.breakpoints.down("xs")]: {
      maxWidth: "90vw",
      maxHeight: 300,
    },
    [theme.breakpoints.up("sm")]: {
      maxWidth: 450,
    },
  },
  list: {
    backgroundColor: theme.palette.shaded.main,
    maxHeight: 400,
    overflowY: "auto",
    width: "100%",
    flexDirection: "column-reverse",
  },
  divider: {
    width: "100%",
  },
  row: {
    width: "100%",
    cursor: "pointer",
  },
  author: {
    width: 30,
    height: 30,
  },
  form: {
    width: "100%",
  },
  text: {
    fontSize: 12,
    wordBreak: "break-word",
    wordWrap: "break-word",
    minWidth: "1%",
  },
  submit: {
    backgroundColor: theme.palette.step.button.background.incomplete,
  },
}))

const AddNoteFooterButton = ({ job, step, isPreview, isScheduled }) => {
  const classes = useStyles()
  const xs = useMediaQuery((theme) => theme.breakpoints.down("xs"))
  const { hasFeature, location } = useAuth()
  const { canDeleteNote } = useJobUtils()
  const [addNote] = useMutationJobStepAddNote(job)
  const [deleteNote, { loading: deleteLoading }] = useMutationJobStepDeleteNote(job)
  const { makeJobStepAddNoteOptimisticResponse } = useMakeJobStepOptimistic()
  const [text, setText] = useState("")
  const [show, setShow] = useState(null)
  const [showDeleting, setShowDeleting] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const notes = useMemo(
    () =>
      step.notes ? [...step.notes].sort((a, b) => (moment(a.createdAt).isAfter(moment(b.createdAt)) ? -1 : 1)) : [],
    [step.notes]
  )

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFocus = () => {
    if (show) {
      setShow(null)
      setShowDeleting(false)
    }
  }

  const handleAdd = async () => {
    if (!text?.trim()) {
      return
    }

    const variables = {
      job,
      step: toId(step),
      text,
    }
    setText("")
    await addNote({ variables, optimisticResponse: makeJobStepAddNoteOptimisticResponse({ ...variables, step }) })
  }

  const handleEnter = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      handleAdd()
    }
  }

  const handleShow = (note) => {
    setShow(show === toId(note) || !canDeleteNote(note) ? null : toId(note))
    setShowDeleting(false)
  }

  const handleDeleting = () => setShowDeleting(true)

  const handleDelete = async (note) => {
    const variables = {
      job,
      step: toId(step),
      note: toId(note),
    }

    await deleteNote({
      variables,
    })
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  if (!hasFeature("process_step_notes")) {
    return null
  }

  const open = Boolean(anchorEl)
  const hasNotes = Boolean(notes?.length)
  const isValid = Boolean(text?.trim())

  return (
    <Box mr={2}>
      <FooterButton
        icon={hasNotes ? "note-added" : "note"}
        iconClassName={hasNotes ? classes.hasNotesIcon : classes.noNotesIcon}
        label={hasNotes ? pluralize("note", notes.length, true) : "Add note"}
        onClick={handleOpen}
        disabled={isPreview || isScheduled}
        data-cy="AddNoteFooterButton-button"
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: xs ? "center" : "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: xs ? "center" : "left",
        }}
      >
        <ColumnBox className={classes.popover}>
          {hasNotes && (
            <ColumnBox className={classes.list} data-cy="AddNoteFooterButton-list">
              {notes.map((item, index) => (
                <React.Fragment key={toId(item)}>
                  {index > 0 && <Divider className={classes.divider} />}
                  <RowBox p={1} alignItems="center" className={classes.row} onClick={() => handleShow(item)}>
                    <Box mr={1}>
                      <Avatar {...item.author} className={classes.author} />
                    </Box>
                    <RowBox flexGrow={1} className={classes.text}>
                      {item.text}
                    </RowBox>
                    <ColumnBox ml={1} alignItems="flex-end" mr={1}>
                      <Collapse in={show !== toId(item)}>
                        <Caption mb={0}>
                          <FormatDateTimeCompact
                            value={item.createdAt}
                            compactTodayTimeZone={location?.timeZone || null}
                          />
                        </Caption>
                      </Collapse>
                      <Collapse in={show === toId(item)}>
                        <FlexBox alignSelf="flex-end" onClick={preventDefault}>
                          <DeleteConfirmIconButton
                            deleting={showDeleting}
                            onDeleting={handleDeleting}
                            onDelete={() => handleDelete(item)}
                            loading={deleteLoading}
                          />
                        </FlexBox>
                      </Collapse>
                    </ColumnBox>
                  </RowBox>
                </React.Fragment>
              ))}
            </ColumnBox>
          )}
          <RowBox p={1} className={classes.form}>
            <FlexBox mr={1} flexGrow={1}>
              <TextField
                size="small"
                placeholder="Add a note..."
                variant="outlined"
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyPress={handleEnter}
                onFocus={handleFocus}
                data-cy="AddNoteFooterButton-note"
                fullWidth
                autoFocus
              />
            </FlexBox>
            <IconButton
              onClick={handleAdd}
              className={classes.submit}
              data-cy="AddNoteFooterButton-add"
              disabled={!isValid}
              size="small"
            >
              <Icon name="done" />
            </IconButton>
          </RowBox>
        </ColumnBox>
      </Popover>
    </Box>
  )
}

export default AddNoteFooterButton
